import { gql, useMutation } from '@apollo/client';
import useForm, { isInvalidEmail, isMissing } from '@energiebespaarders/hooks/useForm';
import { Button, Input, Modal } from '@energiebespaarders/symbols';
import { Small } from '@energiebespaarders/symbols/helpers';
import { useIsMobile } from '@energiebespaarders/symbols/hooks';
import React, { ChangeEvent, useCallback, useState } from 'react';
import {
  forgotPartnerAgentPassword,
  forgotPartnerAgentPasswordVariables,
} from '../../types/generated/forgotPartnerAgentPassword';

interface ForgotPassModalProps {
  closeModal: () => void;
  isOpen: boolean;
}

const FORGOT_PARTNER_AGENT_PASSWORD = gql`
  mutation forgotPartnerAgentPassword($email: String!) {
    forgotPartnerAgentPassword(email: $email)
  }
`;

const ForgotPassModal: React.FC<ForgotPassModalProps> = ({ closeModal, isOpen }) => {
  const mobile = useIsMobile();
  const [successful, setSuccessful] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [forgotPartnerAgentPasswordMutation, { loading }] = useMutation<
    forgotPartnerAgentPassword,
    forgotPartnerAgentPasswordVariables
  >(FORGOT_PARTNER_AGENT_PASSWORD, {
    onCompleted: data => data.forgotPartnerAgentPassword && setSuccessful(true),
    onError: () => setSubmitError('Er is iets misgegaan. Neem contact met ons op.'),
  });

  const { formState, handleChange, submitForm, submissionAttempted } = useForm<{ email: string }>({
    initialValues: { email: '' },
    handleSubmit: values => {
      void forgotPartnerAgentPasswordMutation({ variables: { email: values.email } });
    },
    validate: (values, errors) => {
      if (isMissing(values.email) || isInvalidEmail(values.email)) {
        errors.email = 'Ongeldig e-mailadres';
      }
      return errors;
    },
  });

  const handleChangeEmail = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleChange({ email: e.target.value });
    },
    [handleChange],
  );

  const handleClose = useCallback(() => {
    handleChange({ email: '' });
    setSuccessful(false);
    closeModal();
  }, [closeModal, handleChange]);

  return (
    <Modal
      width="sm"
      isOpen={isOpen}
      title="Wachtwoord herstellen"
      onRequestClose={handleClose}
      mobile={mobile}
    >
      {successful ? (
        <Small>
          <p>
            Als het e-mailadres bij ons bekend is, ontvang je per mail een link om een nieuw
            wachtwoord aan te maken.
          </p>
        </Small>
      ) : (
        <form onSubmit={submitForm}>
          <Small>
            <p>
              Vul hieronder het e-mailadres van jouw Samen account in om een link voor een nieuw
              wachtwoord te ontvangen.
            </p>
          </Small>
          <Input
            type="email"
            label="E-mailadres"
            value={formState.email.value}
            onChange={handleChangeEmail}
            touched={formState.email.touched}
            error={formState.email.error}
            autoComplete="email"
            inputMode="email"
          />
          <Button
            type="submit"
            fluid
            label="Verzenden"
            loading={loading}
            error={submissionAttempted ? submitError : undefined}
          />
        </form>
      )}
    </Modal>
  );
};

export default ForgotPassModal;
